import {SortingState} from '@tanstack/table-core';

export const COLUMN_IDS = {
  post: 'post',
  creator: 'creator',
  followers: 'followers',
  impressions: 'impressions',
  reach: 'reach',
  comments: 'comments',
  likes: 'likes',
  saves: 'saves',
  views: 'views',
  avgWatchTime: 'avgWatchTime',
  videoViewsOrganic: 'videoViewsOrganic',
  videoViewsPaid: 'videoViewsPaid',
  favoritesOrganic: 'favoritesOrganic',
  favoritesPaid: 'favoritesPaid',
  shares: 'shares',
  totalInteractions: 'totalInteractions',
  engagementRate: 'engagementRate',
  emv: 'emv',
} as const;

export type ColumnId = (typeof COLUMN_IDS)[keyof typeof COLUMN_IDS];

export const COLUMN_SIZES: Record<ColumnId, number> = {
  [COLUMN_IDS.post]: 80,
  [COLUMN_IDS.creator]: 180,
  [COLUMN_IDS.followers]: 100,
  [COLUMN_IDS.impressions]: 110,
  [COLUMN_IDS.reach]: 100,
  [COLUMN_IDS.comments]: 100,
  [COLUMN_IDS.likes]: 80,
  [COLUMN_IDS.saves]: 80,
  [COLUMN_IDS.views]: 80,
  [COLUMN_IDS.avgWatchTime]: 120,
  [COLUMN_IDS.videoViewsOrganic]: 110,
  [COLUMN_IDS.videoViewsPaid]: 100,
  [COLUMN_IDS.favoritesOrganic]: 120,
  [COLUMN_IDS.favoritesPaid]: 110,
  [COLUMN_IDS.shares]: 80,
  [COLUMN_IDS.totalInteractions]: 150,
  [COLUMN_IDS.engagementRate]: 140,
  [COLUMN_IDS.emv]: 110,
};

// Single source of truth: Maps table column IDs (camelCase) to API field names (snake_case)
export const COLUMN_ID_TO_API_FIELD: Record<ColumnId, string> = {
  [COLUMN_IDS.post]: 'posted_at',
  [COLUMN_IDS.creator]: '',
  [COLUMN_IDS.followers]: 'follower_count',
  [COLUMN_IDS.impressions]: 'impression_count',
  [COLUMN_IDS.reach]: 'reach_count',
  [COLUMN_IDS.comments]: 'comment_count',
  [COLUMN_IDS.likes]: 'like_count',
  [COLUMN_IDS.saves]: 'saved_count',
  [COLUMN_IDS.views]: 'view_count',
  [COLUMN_IDS.avgWatchTime]: 'video_average_watch_time',
  [COLUMN_IDS.videoViewsOrganic]: 'video_views_organic',
  [COLUMN_IDS.videoViewsPaid]: 'video_views_paid',
  [COLUMN_IDS.favoritesOrganic]: 'favorites_organic',
  [COLUMN_IDS.favoritesPaid]: 'favorites_paid',
  [COLUMN_IDS.shares]: 'share_count',
  [COLUMN_IDS.totalInteractions]: 'total_interactions_count',
  [COLUMN_IDS.engagementRate]: 'engagement_rate',
  [COLUMN_IDS.emv]: '',
};

// Reverse mapping: API field -> table column ID
export const API_FIELD_TO_COLUMN_ID: Record<string, ColumnId> =
  Object.fromEntries(
    Object.entries(COLUMN_ID_TO_API_FIELD)
      .filter(([, apiField]) => apiField !== '')
      .map(([columnId, apiField]) => [apiField, columnId as ColumnId])
  );

export const DEFAULT_SORT = '-posted_at';

const POSTS_SORTABLE_API_FIELDS = new Set([
  'posted_at',
  ...Object.values(COLUMN_ID_TO_API_FIELD).filter((field) => field !== ''),
]);

// Matches PPSuite DATE_FILTER_UNSUPPORTED_SORT_ATTRS. Sorting by these with
// start_date/end_date 400s (no snapshot equivalent; serializer returns null).
export const DATE_FILTER_UNSUPPORTED_SORT_FIELDS = new Set([
  COLUMN_ID_TO_API_FIELD[COLUMN_IDS.favoritesOrganic],
  COLUMN_ID_TO_API_FIELD[COLUMN_IDS.favoritesPaid],
  COLUMN_ID_TO_API_FIELD[COLUMN_IDS.avgWatchTime],
]);

export function postsSortField(sort: string): string {
  return sort.startsWith('-') ? sort.slice(1) : sort;
}

export function normalizePostsSort(
  sort: string | null,
  {dateFiltered = false}: {dateFiltered?: boolean} = {}
): string {
  if (!sort) {
    return DEFAULT_SORT;
  }

  const field = postsSortField(sort);
  if (!POSTS_SORTABLE_API_FIELDS.has(field)) {
    return DEFAULT_SORT;
  }

  if (dateFiltered && DATE_FILTER_UNSUPPORTED_SORT_FIELDS.has(field)) {
    return DEFAULT_SORT;
  }

  return sort;
}

// Parse sort string (e.g., '-posted_at' or 'posted_at') to SortingState
export function parseSortString(sortStr: string): SortingState {
  if (!sortStr) {
    return [];
  }

  const isDesc = sortStr.startsWith('-');
  const apiField = isDesc ? sortStr.slice(1) : sortStr;
  const columnId = API_FIELD_TO_COLUMN_ID[apiField] || (apiField as ColumnId);
  return [{id: columnId, desc: isDesc}];
}
